import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button, Grid, Tab, Tabs } from '@mui/material';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UploadFile } from '@mui/icons-material';

import VideoTabContent from './pixels-studio/VideoTabContent';
import VideoToActionTabContent from './pixels-studio/VideoToActionTabContent';
import ToolBarVideoLibrary from './ToolBarVideoLibrary';
import { ReUploadVideo } from './modal/VideoReUpload';
import { ImportVideoFromLink } from './modal/ImportVideoFromLink';
import VideoFailedTabContent from './pixels-studio/VideoFailedTabContent';
import { OrganisationContext } from '../App';
import VideoImportProgressWS from './notification/VideoImportProgressWS';
import { SimpleNotification } from './notification/SimpleNotification';
import useWebSocket from 'react-use-websocket';
import { TranscriptStatus, VideoStatus } from './notification/types';
import { RootState } from '../index';
import {
  apiVideoToActionCountProvider, requestRHOrganisationLanguage,
  updateImportVideoMessages,
  VideoManagerState
} from '../store/videoManagerSlice';
import { WebSocketMessage } from './types/websocket';

interface DelayFunction {
  (ms: number): Promise<void>;
}

const delay: DelayFunction = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const DELAY_MESSAGE = 5000;

export default function VideoLibrary() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchInputText, setSearchInputText] = useState({
    searchText: '',
  });
  const [errorTabIcon, setErrorTabIcon] = useState<boolean>(false);
  const {
    importVideos, videoToActionCount, videosStale, videoFilterFormat
  } = useSelector<RootState, VideoManagerState>((state) => state.videoManager);
  const { selectedOrganisationObject, selectedOrganisation } = useContext(OrganisationContext);
  const { tab } = useParams();
  const [uploadVideo, setUploadVideo] = useState<boolean>(false);
  const [importFromLink, setImportFromLink] = useState<boolean>(false);
  const WS_URL = process.env.REACT_APP_WS_URL as string;

  const [transcriptMessage, setTranscriptMessage] = useState<string>('');

  useEffect(() => {
    if (selectedOrganisationObject?.is_rightsholder) {
      dispatch({ type: 'RESET_VIDEO_MANAGER' });
      navigate('/video-library');
    }
  }, [selectedOrganisationObject, navigate, dispatch]);

  useEffect(() => {
    if (selectedOrganisation && videosStale) {
      dispatch(apiVideoToActionCountProvider({ publisher: selectedOrganisation }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videosStale]);

  useEffect(() => {
    if (selectedOrganisation) {
      dispatch(apiVideoToActionCountProvider({ publisher: selectedOrganisation }));
      dispatch(requestRHOrganisationLanguage({
        publisher: selectedOrganisation,
        format: videoFilterFormat
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation]);

  useWebSocket(WS_URL, {
      shouldReconnect: () => true,
      reconnectInterval: 3000,
      onMessage: async (message) => {
        const messageData = JSON.parse(message.data) as WebSocketMessage;

        switch (messageData.action) {
          case 'video_transcript': {
            const status = messageData.data as TranscriptStatus;
            if (status.status === 'completed') {
              setTranscriptMessage('Translation completed.')
              setTimeout(() => {
                setTranscriptMessage('')
              }, DELAY_MESSAGE);
            } else if (status.status === 'requested') {
              setTranscriptMessage('Translation requested..')
            } else if (status.status === 'processing') {
              setTranscriptMessage('Translation processing..')
            }
            break;
          }
          // video import activity (social link)
          case 'video_status': {
            const status = messageData.data as VideoStatus;
            let videos: VideoStatus[] = [];
            if (importVideos.find(({ id }) => status.id === id)) {
              videos = importVideos.map(video => video.id === status.id ? status : video);
            } else {
              videos = [...importVideos, status];
            }
            if (/failed|completed/.test(status.status)) {
              setTimeout(() => dispatch(updateImportVideoMessages(importVideos.filter(video => video.id !== status.id))), DELAY_MESSAGE);
            }
            dispatch(updateImportVideoMessages(videos));
            break;
          }
          case 'video_updated': {
            if (selectedOrganisation === messageData.data?.organisation) {
              if (/requires_review/.test(messageData.data?.status)) {
                dispatch({
                  type: 'REMOVE_VIDEO_FROM_LIST',
                  payload: {
                    uuid: messageData.data?.uuid,
                  }
                });
                setTimeout(() => {
                  dispatch({ type: 'UPDATE_VIDEO_LIST_STALE', payload: true });
                }, 2000);
              } else if (/errored/.test(messageData.data?.status)) {
                setErrorTabIcon(true);
                // update status first so the skeleton can show the error icon
                dispatch({
                  type: 'UPDATE_VIDEO_PROPERTY',
                  payload: {
                    uuid: messageData.data?.uuid,
                    status: messageData.data?.status,
                  }
                });
                delay(1000).then(() => {
                  dispatch({
                    type: 'REMOVE_VIDEO_FROM_LIST',
                    payload: {
                      uuid: messageData.data?.uuid,
                    }
                  });
                  return delay(2000);
                }).then(() => {
                  dispatch({ type: 'UPDATE_VIDEO_LIST_STALE', payload: true });
                });
              } else {
                dispatch({ type: 'UPDATE_VIDEO_LIST_STALE', payload: true });
              }
            }
            break;
          }
          case 'video_created': {
            if (selectedOrganisation === messageData.data?.organisation) {
              dispatch({ type: 'UPDATE_VIDEO_LIST_STALE', payload: true });
            }
            break;
          }
          default:
            console.warn('Unknown message', messageData);
        }
      }
    }
  )
  ;

  return (<Fragment>
      <ToolBarVideoLibrary
        title={'Pixels Studio'}
        setSearchInputText={setSearchInputText}
        searchInputText={searchInputText}
        defaultValue={'horizontal'}
        excludeFormat
      />
      <Grid container justifyContent={'flex-end'} gap={1}>
        <Grid item>
          <Button onClick={() => setImportFromLink(true)} startIcon={<UploadFile/>}
                  variant="contained" color="primary">Import from link</Button>
        </Grid>
        <Grid item>
          <Button onClick={() => setUploadVideo(true)} startIcon={<UploadFile/>} variant="contained"
                  color="primary">Upload</Button>
        </Grid>
      </Grid>
      {importFromLink && <ImportVideoFromLink open={Boolean(importFromLink)}
                                              handleClose={() => setImportFromLink(false)}/>}
      {uploadVideo && <ReUploadVideo open={Boolean(uploadVideo)}
                                     title="Upload new video"
                                     handleClose={() => setUploadVideo(false)}
      />}
      <Tabs
        value={tab || 'videos'}
        onChange={(e, value) => {
          dispatch({ type: 'RESET_BEFORE_SWITCH_TAB' });
          navigate(value !== 'videos' ? `/pixels-studio/${value}` : `/pixels-studio`);
        }}
        aria-label="basic tabs example"
        sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
      >
        <Tab label="Videos" value={'videos'}/>
        <Tab label={
          <Badge badgeContent={videoToActionCount} color="primary">
            To action
          </Badge>
        } value={'to-action'}/>
        <Tab label={
          errorTabIcon ? (<Badge badgeContent={'!'} color="error">
            Failed
          </Badge>) : 'Failed'
        } value={'failed'} onClick={() => setErrorTabIcon(false)}/>
      </Tabs>
      {(tab === 'videos' || tab === undefined) && <VideoTabContent/>}
      {tab === 'to-action' && <VideoToActionTabContent/>}
      {tab === 'failed' && <VideoFailedTabContent/>}
      <VideoImportProgressWS/>
      <SimpleNotification message={transcriptMessage}/>
    </Fragment>
  );
}
